import { authHeaders } from '../../auth';
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Room,
  RoomEvent,
  Track,
  RemoteTrack,
  RemoteTrackPublication,
  RemoteParticipant,
} from 'livekit-client';
import { uploadRecording, type AuthHeadersFn } from './phoneSensorApi';

interface Detection {
  bbox: [number, number, number, number];
  class: string;
  conf: number;
}

interface PhoneSensorPlayerProps {
  livekitUrl: string;
  token: string;
  sensorId: number;
  detectionEnabled?: boolean;
  apiUrl?: string;
  authHeaders: AuthHeadersFn;
  onError?: (msg: string) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onRecordingSaved?: () => void;
}

export default function PhoneSensorPlayer({
  livekitUrl, token, sensorId, detectionEnabled, apiUrl,
  authHeaders, onError, onConnected, onDisconnected, onRecordingSaved,
}: PhoneSensorPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const roomRef = useRef<Room | null>(null);
  const detectIntervalRef = useRef<number | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recStartRef = useRef<number>(0);
  const recTimerRef = useRef<number | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [audioMuted, setAudioMuted] = useState(true);
  const [detections, setDetections] = useState<Detection[]>([]);
  const [frameSize, setFrameSize] = useState<{ w: number; h: number } | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recElapsed, setRecElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [liveTime, setLiveTime] = useState('');

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setLiveTime(now.toLocaleTimeString('en-GB', { hour12: false }));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!livekitUrl || !token) return;

    let mounted = true;
    const room = new Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = room;

    const attachTrack = (track: RemoteTrack) => {
      if (track.kind === Track.Kind.Video && videoRef.current) {
        track.attach(videoRef.current);
      } else if (track.kind === Track.Kind.Audio) {
        const el = track.attach();
        el.muted = audioMuted;
        el.dataset.phoneSensorAudio = String(sensorId);
        document.body.appendChild(el);
      }
    };

    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, _participant: RemoteParticipant) => {
      if (!mounted) return;
      attachTrack(track);
    });

    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      track.detach();
    });

    room.on(RoomEvent.Disconnected, () => {
      if (!mounted) return;
      setConnected(false);
      onDisconnected?.();
    });

    const connect = async () => {
      setConnecting(true);
      try {
        await room.connect(livekitUrl, token);
        if (!mounted) return;
        setConnected(true);
        setConnecting(false);
        onConnected?.();

        room.remoteParticipants.forEach((participant: RemoteParticipant) => {
          participant.trackPublications.forEach((pub: RemoteTrackPublication) => {
            if (pub.track && pub.isSubscribed) {
              attachTrack(pub.track as RemoteTrack);
            }
          });
        });
      } catch (err: unknown) {
        if (!mounted) return;
        setConnecting(false);
        onError?.(err instanceof Error ? err.message : 'Failed to connect');
      }
    };

    connect();

    return () => {
      mounted = false;
      room.disconnect();
      roomRef.current = null;
      document.querySelectorAll(`[data-phone-sensor-audio="${sensorId}"]`).forEach((el) => el.remove());
      setConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livekitUrl, token]);

  const toggleAudio = useCallback(() => {
    const els = document.querySelectorAll<HTMLMediaElement>(`[data-phone-sensor-audio="${sensorId}"]`);
    const newMuted = !audioMuted;
    els.forEach((el) => { el.muted = newMuted; });
    setAudioMuted(newMuted);
  }, [audioMuted, sensorId]);

  const startRecording = useCallback(() => {
    const video = videoRef.current;
    if (!video || !connected) return;
    const vid = video as any;
    const stream: MediaStream = vid.captureStream ? vid.captureStream() : vid.mozCaptureStream();
    const audioEls = document.querySelectorAll<HTMLMediaElement>(`[data-phone-sensor-audio="${sensorId}"]`);
    audioEls.forEach(el => {
      const src = (el as HTMLMediaElement & { captureStream?(): MediaStream; srcObject?: MediaStream | null }).srcObject as MediaStream | null;
      if (src) {
        src.getAudioTracks().forEach(t => stream.addTrack(t));
      }
    });

    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : 'video/webm';
    const recorder = new MediaRecorder(stream, { mimeType });
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.start(1000);
    recorderRef.current = recorder;
    recStartRef.current = Date.now();
    setIsRecording(true);
    setRecElapsed(0);
    recTimerRef.current = window.setInterval(() => {
      setRecElapsed(Math.floor((Date.now() - recStartRef.current) / 1000));
    }, 1000);
  }, [connected]);

  const stopRecording = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;

    const duration = (Date.now() - recStartRef.current) / 1000;
    if (recTimerRef.current) clearInterval(recTimerRef.current);
    recTimerRef.current = null;

    await new Promise<void>(resolve => {
      recorder.onstop = () => resolve();
      recorder.stop();
    });

    const blob = new Blob(chunksRef.current, { type: 'video/webm' });
    chunksRef.current = [];
    recorderRef.current = null;
    setIsRecording(false);
    setRecElapsed(0);

    if (blob.size < 1000) return;

    setUploading(true);
    try {
      await uploadRecording(sensorId, blob, duration, authHeaders);
      onRecordingSaved?.();
    } catch {
      onError?.('Failed to save recording');
    } finally {
      setUploading(false);
    }
  }, [sensorId, authHeaders, onError, onRecordingSaved]);

  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
      if (recTimerRef.current) clearInterval(recTimerRef.current);
    };
  }, []);

  const captureAndDetect = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !apiUrl || video.videoWidth === 0) return;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(video, 0, 0);

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.7)
      );
      if (!blob) return;

      const formData = new FormData();
      formData.append('file', blob, 'frame.jpg');

      const resp = await fetch(`${apiUrl}/api/drone/detect-frame`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      if (resp.ok) {
        const data = await resp.json();
        setDetections(data.detections || []);
        setFrameSize({ w: data.frame_width, h: data.frame_height });
      }
    } catch { /* detection is optional */ }
  }, [apiUrl]);

  useEffect(() => {
    if (detectionEnabled && connected) {
      detectIntervalRef.current = window.setInterval(captureAndDetect, 500);
    } else {
      if (detectIntervalRef.current) {
        clearInterval(detectIntervalRef.current);
        detectIntervalRef.current = null;
      }
      setDetections([]);
    }
    return () => {
      if (detectIntervalRef.current) clearInterval(detectIntervalRef.current);
    };
  }, [detectionEnabled, connected, captureAndDetect]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || !frameSize) return;

    canvas.width = video.clientWidth;
    canvas.height = video.clientHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (detections.length === 0) return;

    const scaleX = canvas.width / frameSize.w;
    const scaleY = canvas.height / frameSize.h;

    for (const det of detections) {
      const [x1, y1, x2, y2] = det.bbox;
      const sx = x1 * scaleX;
      const sy = y1 * scaleY;
      const sw = (x2 - x1) * scaleX;
      const sh = (y2 - y1) * scaleY;

      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 2;
      ctx.strokeRect(sx, sy, sw, sh);

      const label = `${det.class} ${det.conf}`;
      ctx.font = '12px monospace';
      const tm = ctx.measureText(label);
      ctx.fillStyle = '#00ff00';
      ctx.fillRect(sx, sy - 16, tm.width + 6, 16);
      ctx.fillStyle = '#000';
      ctx.fillText(label, sx + 3, sy - 4);
    }
  }, [detections, frameSize]);

  const fmtTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          pointerEvents: 'none',
        }}
      />

      {connecting && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.7)', color: 'var(--color-accent)', fontSize: 14,
        }}>
          Connecting to PHONECAM...
        </div>
      )}

      {connected && (
        <>
          <div style={{
            position: 'absolute', top: 8, left: 8,
            background: 'rgba(0,0,0,0.6)', padding: '2px 8px',
            borderRadius: 4, fontSize: 11, color: 'var(--color-accent)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>{'●'} PHONECAM LIVE</span>
            {detectionEnabled && detections.length > 0 && <span>| {detections.length} detections</span>}
            <span style={{ color: 'var(--color-text-secondary)' }}>{liveTime}</span>
          </div>

          <div style={{
            position: 'absolute', bottom: 8, left: 8, right: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={uploading}
              style={{
                background: isRecording ? 'rgba(255,0,0,0.8)' : 'rgba(0,0,0,0.6)',
                border: isRecording ? '1px solid var(--color-danger)' : '1px solid var(--color-border-light)',
                borderRadius: 4, padding: '4px 10px', cursor: uploading ? 'not-allowed' : 'pointer',
                color: isRecording ? '#fff' : 'var(--color-text-primary)', fontSize: 11,
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              {uploading ? 'Saving...' : isRecording ? (
                <><span style={{ color: 'var(--color-danger)', fontSize: 14 }}>{'●'}</span> REC {fmtTime(recElapsed)} — Stop</>
              ) : (
                <><span style={{ color: 'var(--color-danger)', fontSize: 14 }}>{'●'}</span> REC</>
              )}
            </button>

            <button
              onClick={toggleAudio}
              style={{
                background: 'rgba(0,0,0,0.6)', border: '1px solid var(--color-border-light)',
                borderRadius: 4, padding: '4px 8px', cursor: 'pointer',
                color: audioMuted ? 'var(--color-text-secondary)' : 'var(--color-accent)', fontSize: 11,
              }}
            >
              {audioMuted ? '\u{1F507} Unmute' : '\u{1F50A} Mute'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
